import { Autocomplete, Box, CircularProgress } from '@mui/material';
import { ChangeEvent, DragEvent, ReactNode, useState } from 'react';

import { Icon } from '@/components/common/Icon';
import { Field, IconButton, Meta, Segmented, TextInput } from '@/components/ds';

// The pieces both "Add a property" dialogs share (the dashboard's and the admin Properties page's), so the two read
// the same: the address search as a ds well, the resolved address as a quiet line, and the report as a sleeve tile.

export const ADD_PROPERTY_TITLE_SX = { fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em' } as const;

// ── Address search ──────────────────────────────────────────────────────────

export interface AddressSearchProps {
  id: string;
  options: google.maps.places.AutocompletePrediction[];
  value: google.maps.places.AutocompletePrediction | null;
  onChange: (value: google.maps.places.AutocompletePrediction | null) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  loading: boolean;
  testId?: string;
}

export const AddressSearch = ({
  id,
  options,
  value,
  onChange,
  inputValue,
  onInputChange,
  loading,
  testId,
}: AddressSearchProps) => (
  <Field label="Address" htmlFor={id}>
    <Autocomplete
      id={id}
      freeSolo
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      value={value}
      onChange={(_, newValue) => {
        if (typeof newValue !== 'string') onChange(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => onInputChange(newInputValue)}
      loading={loading}
      renderInput={(params) => (
        <TextInput
          ref={params.InputProps.ref}
          className={params.InputProps.className}
          inputProps={params.inputProps}
          disabled={params.disabled}
          fullWidth
          icon="search"
          placeholder="Start typing an address…"
          data-testid={testId}
          endAdornment={
            <>
              {loading && <CircularProgress size={14} sx={{ color: 'var(--tx-muted)', flex: 'none' }} />}
              {params.InputProps.endAdornment}
            </>
          }
        />
      )}
      renderOption={(props, option) => (
        <li
          {...props}
          key={option.place_id}
          data-testid="add-property-address-suggestion"
          style={{ fontSize: 13, fontWeight: 500, color: 'var(--tx-text)', padding: '10px 14px' }}>
          {option.description}
        </li>
      )}
      slotProps={{
        paper: {
          // the floating list: the dialog's solid material, raised, like the app's drop-down panels
          sx: {
            mt: '6px',
            backgroundColor: 'var(--tx-dialog)',
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
            border: 0,
            borderRadius: '12px',
            '& .MuiAutocomplete-listbox': { p: '6px' },
            '& .MuiAutocomplete-option': {
              borderRadius: '9px',
              '&:hover, &.Mui-focused': { backgroundColor: 'var(--tx-fill) !important' },
              '&[aria-selected="true"]': { backgroundColor: 'var(--tx-fill-2) !important' },
            },
            '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--tx-faint)',
            },
          },
        },
      }}
    />
  </Field>
);

// ── Resolved address ────────────────────────────────────────────────────────

// the address Google resolved, as a quiet line under the search (a pin, the street, the city), not a boxed tile
export const AddressPreview = ({ street, locality, testId }: { street: string; locality: string; testId?: string }) => (
  <Box data-testid={testId} sx={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0, pt: '2px' }}>
    <Box component="span" sx={{ display: 'flex', color: 'var(--tx-muted)', mt: '1px', flex: 'none' }}>
      <Icon name="pin" size={16} stroke={1.6} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--tx-text)' }}>{street}</Box>
      <Box sx={{ fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', mt: '2px' }}>{locality}</Box>
    </Box>
  </Box>
);

// ── Report sleeve ───────────────────────────────────────────────────────────

// the records browser's empty sleeve (.hx-sleeve-add) at dialog scale: a small dashed 3:4 sheet with two faint sheets
// fanned behind it, beside its caption. The whole row is the click and drop target; no upload button, no upload box.
const SLEEVE_CSS = `
  .hx-report-sleeve { display: flex; align-items: center; gap: 18px; width: 100%; padding: 6px 4px 6px 10px; border: 0;
    border-radius: 14px; background: transparent; cursor: pointer; font: inherit; color: inherit; text-align: left; }
  .hx-report-sleeve .sheet { position: relative; width: 54px; aspect-ratio: 3 / 4; flex: none; }
  .hx-report-sleeve .ghost { position: absolute; inset: 0; border-radius: 9px;
    border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.12); transition: transform 0.45s cubic-bezier(0.32, 0.72, 0, 1); }
  .hx-report-sleeve .ghost.l { transform: rotate(-7deg) translate(-4px, 2px); }
  .hx-report-sleeve .ghost.r { transform: rotate(6deg) translate(4px, 1px); }
  .hx-report-sleeve .face { position: absolute; inset: 0; border-radius: 9px; display: grid; place-items: center;
    border: 1.5px dashed rgba(var(--tx-ink-rgb), 0.28); background: var(--tx-glass-panel); color: var(--tx-muted);
    transition: transform 0.45s cubic-bezier(0.32, 0.72, 0, 1), border-color 0.15s ease, color 0.15s ease; }
  .hx-report-sleeve .plus { width: 28px; height: 28px; border-radius: 50%; display: grid; place-items: center;
    background: var(--tx-fill); color: var(--tx-text); transition: background 0.15s ease; }
  .hx-report-sleeve:hover .face, .hx-report-sleeve:focus-visible .face, .hx-report-sleeve.drag .face {
    transform: translateY(-3px); border-color: rgba(var(--tx-ink-rgb), 0.5); color: var(--tx-text); }
  .hx-report-sleeve:hover .plus, .hx-report-sleeve.drag .plus { background: var(--tx-fill-2); }
  .hx-report-sleeve:hover .ghost.l, .hx-report-sleeve.drag .ghost.l { transform: rotate(-11deg) translate(-8px, 3px); }
  .hx-report-sleeve:hover .ghost.r, .hx-report-sleeve.drag .ghost.r { transform: rotate(10deg) translate(8px, 2px); }
  .hx-report-sleeve:focus-visible { outline: 2px solid var(--tx-focus); outline-offset: 2px; }
  .hx-report-sleeve .filed { width: 54px; aspect-ratio: 3 / 4; flex: none; border-radius: 9px; display: grid;
    place-items: center; background: var(--tx-fill-2); color: var(--tx-muted); }
  @media (prefers-reduced-motion: reduce) { .hx-report-sleeve .face, .hx-report-sleeve .ghost { transition: none; } }
`;

export interface ReportSleeveProps {
  /** id of the hidden file input (tests and e2e reach it by this id) */
  inputId: string;
  label: string;
  file: File | null;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** called with the first dropped file (or undefined); validation stays with the caller */
  onDropFile: (file: File | undefined) => void;
  onRemove: () => void;
  reportSource: 'seller' | 'buyer';
  onReportSourceChange: (source: 'seller' | 'buyer') => void;
  /** phones can't drop, so the caption only offers the pick */
  isMobile?: boolean;
  testId?: string;
}

export const ReportSleeve = ({
  inputId,
  label,
  file,
  onInputChange,
  onDropFile,
  onRemove,
  reportSource,
  onReportSourceChange,
  isMobile,
  testId,
}: ReportSleeveProps) => {
  const [dragging, setDragging] = useState(false);

  const stop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  let body: ReactNode;
  if (file) {
    // filed: the sleeve now holds a sheet, beside its name, size and who the report came from
    body = (
      <Box className="hx-report-sleeve" sx={{ cursor: 'default !important' }}>
        <span className="filed" aria-hidden>
          <Icon name="quotedoc" size={18} stroke={1.6} />
        </span>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'var(--tx-text)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {file.name}
            </Box>
            <Meta>{(file.size / (1024 * 1024)).toFixed(2)} MB</Meta>
          </Box>
          <Segmented
            size="sm"
            label="Report from"
            value={reportSource}
            onChange={onReportSourceChange}
            options={[
              ['seller', 'Seller'],
              ['buyer', 'Buyer'],
            ]}
          />
        </Box>
        <IconButton icon="x" label="Remove file" size="sm" variant="ghost" onClick={onRemove} sx={{ color: 'var(--tx-muted)' }} />
      </Box>
    );
  } else {
    body = (
      <button
        type="button"
        className={`hx-report-sleeve${dragging ? ' drag' : ''}`}
        aria-label={`Add ${label.toLowerCase()}`}
        data-testid={testId}
        onClick={() => document.getElementById(inputId)?.click()}
        onDragOver={(e) => {
          stop(e);
          setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
        }}
        onDrop={(e) => {
          stop(e);
          setDragging(false);
          onDropFile(e.dataTransfer.files?.[0]);
        }}>
        <span className="sheet" aria-hidden>
          <span className="ghost l" />
          <span className="ghost r" />
          <span className="face">
            <span className="plus">
              <Icon name="plus" size={14} stroke={1.8} />
            </span>
          </span>
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--tx-text)' }}>
            Add a file
          </span>
          <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>
            {isMobile ? 'PDF, DOCX, JPG or PNG up to 25 MB' : 'or drop one here · PDF, DOCX, JPG or PNG up to 25 MB'}
          </span>
        </span>
      </button>
    );
  }

  return (
    <Field label={label} aside={<Meta>Optional</Meta>}>
      <style>{SLEEVE_CSS}</style>
      {body}
      <input
        id={inputId}
        type="file"
        accept=".pdf,.docx,.jpg,.jpeg,.png"
        onChange={onInputChange}
        style={{ display: 'none' }}
      />
    </Field>
  );
};
